// client/src/components/reviews/ReviewForm.js
import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, FormControlLabel, Switch,
  Typography, CircularProgress, Alert, Chip,
} from '@mui/material';
import axiosInstance from '../../axiosInstance';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

export default function ReviewForm({ collectionType, collectionItemId, onCreated, onCancel }) {
  const { featureFlags } = useFeatureFlags();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [containsSpoilers, setContainsSpoilers] = useState(false);
  const [moodTags, setMoodTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (featureFlags.enableRicherReactions) {
      axiosInstance.get('/reviews/mood-tags').then(res => setAvailableTags(res.data)).catch(() => {});
    }
  }, [featureFlags.enableRicherReactions]);

  const toggleTag = (tag) => {
    setMoodTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : prev.length < 3 ? [...prev, tag] : prev
    );
  };

  const handleSubmit = async () => {
    if (!title.trim() || !body.trim()) {
      setError('Title and review body are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await axiosInstance.post('/reviews', {
        collectionType, collectionItemId, title: title.trim(), body: body.trim(),
        containsSpoilers,
        ...(featureFlags.enableRicherReactions && { moodTags }),
      });
      onCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit review.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Write a Review</Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <TextField
        label="Title" size="small" fullWidth value={title}
        onChange={e => setTitle(e.target.value)} inputProps={{ maxLength: 120 }} sx={{ mb: 1 }}
      />
      <TextField
        label="Your review" multiline rows={4} fullWidth value={body}
        onChange={e => setBody(e.target.value)} inputProps={{ maxLength: 5000 }} sx={{ mb: 1 }}
      />
      {featureFlags.enableRicherReactions && availableTags.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Mood tags (pick up to 3{moodTags.length === 3 ? ' — max reached' : ''})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {availableTags.map(tag => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onClick={() => toggleTag(tag)}
                color={moodTags.includes(tag) ? 'primary' : 'default'}
                variant={moodTags.includes(tag) ? 'filled' : 'outlined'}
                clickable
                disabled={!moodTags.includes(tag) && moodTags.length >= 3}
              />
            ))}
          </Box>
        </Box>
      )}
      <FormControlLabel
        control={<Switch checked={containsSpoilers} onChange={e => setContainsSpoilers(e.target.checked)} />}
        label="Contains spoilers"
      />
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <CircularProgress size={18} /> : 'Submit'}
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </Box>
    </Box>
  );
}
